import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} gallery - The zoomed gallery element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'gallery', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #sourceSignature = '';

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.gallery.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.gallery.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const preparedMedia = this.prepare(index);
    const { dialog, media, thumbnails } = this.refs;
    const targetImage = preparedMedia || media?.[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Creates the zoom gallery only after a customer shows intent to use it.
   * @param {number} index - The media index that is about to be shown.
   * @returns {HTMLElement | undefined}
   */
  prepare(index = 0) {
    const sourceMedia = this.#getSourceMedia();
    const sourceSignature = sourceMedia.map((media) => this.#getMediaId(media)).join('|');

    if (!this.refs.media?.length || sourceSignature !== this.#sourceSignature) {
      this.#renderContent(sourceMedia);
      this.#sourceSignature = sourceSignature;
    }

    const targetMedia = this.refs.media?.[index];
    if (targetMedia) this.loadHighResolutionImage(targetMedia);

    return targetMedia;
  }

  /** @returns {HTMLElement[]} */
  #getSourceMedia() {
    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | null} */ (this.closest('media-gallery'));
    const sourceMedia = mediaGallery?.presentation === 'carousel' ? mediaGallery.slideshow?.slides : mediaGallery?.media;

    return Array.from(sourceMedia || []).filter((media) => media instanceof HTMLElement);
  }

  /**
   * @param {HTMLElement} source
   * @returns {string}
   */
  #getMediaId(source) {
    return source.querySelector('.product-media')?.getAttribute('data-media-id') || '';
  }

  /**
   * @param {HTMLElement[]} sourceMedia
   */
  #renderContent(sourceMedia) {
    const { gallery, thumbnails } = this.refs;
    const galleryFragment = document.createDocumentFragment();
    const thumbnailFragment = document.createDocumentFragment();

    sourceMedia.forEach((source, index) => {
      const sourceProductMedia = source.querySelector('.product-media');
      const sourceImage = sourceProductMedia?.querySelector('img.product-media__image');
      if (!(sourceProductMedia instanceof HTMLElement) || !(sourceImage instanceof HTMLImageElement)) return;

      const mediaId = this.#getMediaId(source);
      const mediaType = this.#getMediaType(source);
      const aspectRatio = sourceProductMedia.style.getPropertyValue('--ratio') || '1';
      const productMedia = /** @type {HTMLElement} */ (sourceProductMedia.cloneNode(true));
      const productImage = productMedia.querySelector('img.product-media__image');
      const mediaContainer = document.createElement('li');

      mediaContainer.id = `product-${mediaId}-${index + 1}`;
      mediaContainer.className = this.#getZoomMediaClassName(source, mediaType);
      mediaContainer.style.setProperty('--media-preview-ratio', aspectRatio);
      mediaContainer.setAttribute('ref', 'media[]');

      if (productImage instanceof HTMLImageElement) {
        productImage.loading = 'lazy';
        productImage.fetchPriority = 'auto';
        productImage.sizes = '100vw';
      }

      if (mediaType === 'image') {
        mediaContainer.setAttribute('on:click', '/close');
        productImage?.setAttribute('ref', 'image');

        const zoomWrapper = document.createElement('drag-zoom-wrapper');
        zoomWrapper.className = 'product-media__drag-zoom-wrapper';
        zoomWrapper.append(productMedia);
        mediaContainer.append(zoomWrapper);
      } else {
        mediaContainer.append(productMedia);
      }

      galleryFragment.append(mediaContainer);
      thumbnailFragment.append(this.#createThumbnail(sourceImage, mediaType, aspectRatio, index));
    });

    gallery.replaceChildren(galleryFragment);
    thumbnails.replaceChildren(thumbnailFragment);
    this.updatedCallback();
  }

  /**
   * @param {HTMLElement} source
   * @returns {string}
   */
  #getMediaType(source) {
    const typeClass = Array.from(source.classList).find((className) =>
      ['image', 'video', 'external_video', 'model'].some(
        (mediaType) => className === `product-media-container--${mediaType}`
      )
    );

    return typeClass?.replace('product-media-container--', '') || 'image';
  }

  /**
   * @param {HTMLElement} source
   * @param {string} mediaType
   * @returns {string}
   */
  #getZoomMediaClassName(source, mediaType) {
    const classes = Array.from(source.classList).filter(
      (className) =>
        className !== 'media-fit-cover' &&
        className !== 'product-media-container--zoomable' &&
        className !== 'product-media-container--iicase-feature-video'
    );

    if (!classes.includes('product-media-container')) classes.unshift('product-media-container');
    if (!classes.includes(`product-media-container--${mediaType}`)) classes.push(`product-media-container--${mediaType}`);
    if (mediaType === 'image') classes.push('product-media-container--zoomable');

    return classes.join(' ');
  }

  /**
   * @param {HTMLImageElement} sourceImage
   * @param {string} mediaType
   * @param {string} aspectRatio
   * @param {number} index
   * @returns {HTMLButtonElement}
   */
  #createThumbnail(sourceImage, mediaType, aspectRatio, index) {
    const thumbnail = document.createElement('button');
    const thumbnailImage = /** @type {HTMLImageElement} */ (sourceImage.cloneNode(true));
    const label = sourceImage.alt || mediaType;

    thumbnail.type = 'button';
    thumbnail.className = 'button button-unstyled dialog-thumbnails-list__thumbnail';
    thumbnail.setAttribute('aria-label', `Scroll to ${label}`);
    thumbnail.setAttribute('on:click', `/handleThumbnailClick/${index}`);
    thumbnail.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
    thumbnail.style.setProperty('--aspect-ratio', aspectRatio);
    thumbnail.style.setProperty('--gallery-aspect-ratio', aspectRatio);
    if (index === 0) thumbnail.setAttribute('aria-selected', 'true');

    thumbnailImage.removeAttribute('class');
    thumbnailImage.removeAttribute('ref');
    thumbnailImage.removeAttribute('fetchpriority');
    thumbnailImage.removeAttribute('data-max-resolution');
    thumbnailImage.removeAttribute('data_max_resolution');
    thumbnailImage.loading = 'lazy';
    thumbnailImage.sizes = '110px';
    thumbnail.append(thumbnailImage);

    return thumbnail;
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data-max-resolution') || image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.style.cssText = image.style.cssText;
    newImage.setAttribute('data-max-resolution', highResolutionUrl);
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;
    if (!media?.length) return;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;
    if (!media?.length) return this.closeDialog();

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media?.[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media = [], thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
